import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { BASE_URL } from "../BaseUrl";
import { getCookie } from "../functions/Functions";

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useOptimizedSelect = (
  endpoint,
  {
    debounceMs = 2000,
    selectedOption = null,
    responseIndex = 0,
    onEmptyResponse = null,
    onError = null,
  } = {}
) => {
  const [inputValue, setInputValue] = useState("");
  const [fetchedOptions, setFetchedOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [noOptionMessage, setNoOptionMessage] = useState('Enter Phone No To Search');

  const onEmptyResponseRef = React.useRef(onEmptyResponse);
  const onErrorRef = React.useRef(onError);

  React.useEffect(() => {
    onEmptyResponseRef.current = onEmptyResponse;
    onErrorRef.current = onError;
  }, [onEmptyResponse, onError]);

  const handleDebounce = useCallback(async (searchValue, signal) => {
    const url = BASE_URL + endpoint;
    const key = /^\+?[\d\s()-]+$/.test(searchValue) ? "customer_phn_no" : "name";

    try {
      const res = await axios.post(url, { [key]: searchValue }, {
        headers: { Authorization: `Bearer ${getCookie()}` },
        signal: signal,
      });
      
      const data = res.data;
      if (data) {
        let results = [];
        if (responseIndex !== null && Array.isArray(data) && data[responseIndex]) {
          results = data[responseIndex];
        } else {
          results = Array.isArray(data) ? data : [data];
        }
        setFetchedOptions(results);
        
        if (results.length === 0) {
          setNoOptionMessage('No Customer Exist');
          if (onEmptyResponseRef.current) onEmptyResponseRef.current(searchValue);
        }
      } else {
        setFetchedOptions([]);
        setNoOptionMessage('No Customer Exist');
      }
      setIsLoading(false);
      
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled:", error.message);
        return;
      }
      console.error("Optimized Select Fetch Error:", error);
      setFetchedOptions([]);
      setIsLoading(false);
      if (onErrorRef.current) onErrorRef.current(error);
    }
  }, [endpoint, responseIndex]);

  useEffect(() => {
    if (inputValue !== "" && inputValue.length > 0) {
      setIsLoading(true);
      const abortController = new AbortController();
      const timer = setTimeout(() => {
        handleDebounce(inputValue, abortController.signal);
      }, debounceMs);

      return () => {
        clearTimeout(timer);
        abortController.abort();
      };
    } else {
      setFetchedOptions([]);
      setIsLoading(false);
      setNoOptionMessage('Enter Phone No To Search');
    }
  }, [inputValue, debounceMs, handleDebounce]);

  const onInputChange = useCallback((newInputValue, { action }) => {
    if (action === "input-change") {
      setInputValue(newInputValue);
    } else if (action === "set-value" || action === "menu-close") {
      setInputValue("");
    }
  }, []);

  const options = React.useMemo(() => {
    if (inputValue === "" && selectedOption) {
      return [selectedOption];
    }
    return fetchedOptions;
  }, [inputValue, fetchedOptions, selectedOption]);

  return {
    inputValue,
    onInputChange,
    options,
    isLoading,
    noOptionMessage,
  };
};
